// Client
export { LocalAgentSDK } from './client.js';
export type { SDKConfig, RetryConfig, RequestOptions } from './http.js';
export {
  newerWorkspaceProjection,
  parseWorkspaceProjection,
  parseWorkspaceProjectionList,
  workspaceProjectionFromBridgeFrame,
} from './workspace-projection.js';
export {
  WORKSPACE_CONTEXT_OBSERVED,
  parseWorkspaceContextObserved,
} from './workspace-context-observed.js';

// Auth providers
export type { AuthProvider } from './auth/types.js';
export { StaticTokenProvider } from './auth/static.js';
export { CallbackAuthProvider } from './auth/callback.js';
export type { CallbackAuthProviderOptions } from './auth/callback.js';

// Auth state machine — the cross-surface contract. The SDK is the sole
// manufacturer of `SessionVerdict` and the sole owner of the phase reducer;
// no surface may compute a HolderPhase for itself. See src/auth/authState.ts.
export {
  classifyVerdict,
  applyEvent,
  applyError,
  INITIAL_PHASE,
  VERDICT_ACTIVE,
} from './auth/authState.js';
export type {
  SessionVerdict,
  RejectedCause,
  NotActivatedCause,
  UnknownVerdictCause,
  HolderPhase,
  HolderPhaseName,
  DegradedReason,
  HolderEvent,
  SignedOutReason,
  AcquisitionSource,
  CredentialIdentity,
  CredentialMaterial,
  TransitionResult,
  TransitionRefusal,
  RefusalReason,
} from './auth/authState.js';

// The capability table — the ONE module that enumerates phases. Every consumer
// that needs to know what a phase MEANS imports a derivation from here.
export {
  PHASE_CAPABILITIES,
  ALL_HOLDER_PHASES,
  capabilitiesOf,
  holdsMaterial,
  holdsIdentity,
  servesBearer,
  blocksUI,
  isRenewable,
  materialOf,
  identityOf,
  expiresAtOf,
  degradedReasonOf,
  acquisitionSourceOf,
  bearerOf,
} from './lifecycle/capabilities.js';
export type { PhaseOrName } from './lifecycle/capabilities.js';

// The ONE session-JWT decoder.
export {
  decodeSessionJwt,
  jwtStringClaim,
  credentialFingerprint,
} from './auth/decodeSessionJwt.js';
export type { SessionJwtClaims } from './auth/decodeSessionJwt.js';

// Errors
export {
  SDKError,
  APIError,
  NetworkError,
  TimeoutError,
  RequestCancelledError,
  MissingCredentialError,
  PersonaConfigIncompleteError,
  MalformedJwtError,
  ShogunChopHTTPError,
  isAPIError,
  isNotFound,
  isConflict,
  isForbidden,
  isNetworkError,
  isTimeoutError,
  isRequestCancelledError,
  isMissingCredentialError,
  isMalformedJwtError,
} from './errors.js';

// Resource classes
export { SessionsResource } from './resources/sessions.js';
export { DeviceResource } from './resources/device.js';
export { PersonaResource } from './resources/persona.js';
export { SecretsResource } from './resources/secrets.js';
export { SkillsResource } from './resources/skills.js';
export { MediaResource } from './resources/media.js';
export type {
  MediaObject,
  MediaVersion,
  MediaFormat,
  ListMediaParams,
  ListMediaResponse,
  MediaUsage,
  MediaContentParams,
  MediaRestoreResult,
  MediaUploadParams,
  MediaBulkAction,
  MediaBulkParams,
  MediaBulkResult,
} from './types/media.js';
export { AuthResource } from './resources/auth.js';
export type { WorkstationTermsStatus, AcceptWorkstationTermsRequest } from './resources/account.js';
// Auth/identity types declared on the resource —
// the canonical contract the web UI + tests import from the package root.
export type {
  OrgMembership,
  OrgMember,
  ListMembersResponse,
  InviteMemberResponse,
  ActivatedSessionResponse,
  ListedOrganization,
  CreateOrganizationResponse,
  RedeemDesktopHandoffResponse,
  InviteMode,
  InvitePolicy,
  SetInvitePolicyRequest,
} from './resources/auth.js';
export { BillingResource } from './resources/billing.js';
export { WorkManagementResource } from './resources/work-management.js';
export { OrgChartResource } from './resources/orgchart.js';
export { ReasoningResource } from './resources/reasoning.js';
export { ToolsResource } from './resources/tools.js';
export { EventsResource } from './resources/events.js';
export { ErrorQueriesResource } from './resources/error-queries.js';
export { NotificationsResource } from './resources/notifications.js';
export { FeedResource } from './resources/feed.js';
export { SupportResource } from './resources/support.js';
export { LiveFeed, LIVE_FEED_PAGE_SIZE, LIVE_FEED_WINDOW_MAX } from './feed/live-feed.js';
export { AutomationResource } from './resources/automation.js';
export {
  AgentProcessesResource,
  AgentProcessStepTemplatesResource,
} from './resources/agent-process.js';
export {
  ContextResource,
  ContextSourcesResource,
  ContextProfileResource,
  ContextWorkspaceResource,
} from './resources/context.js';
export { IntegrationsResource } from './resources/integrations.js';
export { FileWriteConflictError } from './resources/workspaces.js';
export { MCPResource } from './resources/mcp.js';
export { BridgeResource } from './resources/bridge.js';
export type { BridgeStatus, ConnectedDevice } from './resources/bridge.js';
export {
  BridgeEventStream,
  deriveBridgeUrl,
  BRIDGE_WS_PROTOCOL,
  bridgeAuthProtocols,
} from './resources/bridge-events.js';
export {
  CatalogsResource,
  CatalogHolderRegistry,
  CATALOG_UNSUBSCRIBE_DEBOUNCE_MS,
  CATALOG_SCOPES,
} from './resources/catalogs.js';
export type {
  CatalogChanged,
  CatalogChangedHandler,
  CatalogRegistrar,
  CatalogScopeType,
} from './resources/catalogs.js';
export type {
  IndexingProgress,
  IndexingProgressHandler,
  WorkspaceFilesChanged,
  WorkspaceFilesChangedHandler,
  WorkspaceContextObservedHandler,
  WorkspaceProjectionHandler,
  WorkspaceProjectionRegistrar,
  BridgeIdentity,
  SessionEventHandler,
  NotificationRegistrar,
  FeedRegistrar,
  NotificationCursorStore,
  NotificationEventHandler,
  ErrorEventHandler,
  FeedEventHandler,
  SupportEventHandler,
  PushFrameMeta,
} from './resources/bridge-events.js';
export { ShogunChopPushFrameStream } from './resources/shogunchop-push-frames.js';
export { HttpSessionRegistrar, BridgeSessionSource } from './resources/bridge-session-source.js';
export type { SessionRegistrar } from './resources/bridge-session-source.js';
export { AccountManagementResource } from './resources/account-management.js';
export { AccountResource } from './resources/account.js';
export { AccountRolesResource, LastOwnerError } from './resources/rbac.js';
export { SearchResource } from './resources/search.js';
export { DocsCorpusResource } from './resources/docs-corpus.js';
export { AdminResource } from './resources/admin.js';
export { AgentSettingsResource } from './resources/agent-settings.js';
export type {
  AgentSettingRow,
  AgentSettingsList,
} from './resources/agent-settings.js';
export { ReservedBlocksResource } from './resources/reserved-blocks.js';
export type {
  ReservedBlock,
  ReservedBlocksList,
} from './resources/reserved-blocks.js';
export {
  ShogunChopResource,
  ShogunChopEventStream,
  createDaemonCatalogRegistrar,
  browserWebSocketFactory,
  SHOGUNCHOP_EVENTS_PROTOCOL,
  SHOGUNCHOP_CLOSE_LAG,
  SHOGUNCHOP_CLOSE_REAUTH_REQUIRED,
} from './resources/shogunchop.js';
export type {
  CatalogHoldOp,
  ShogunChopEventHandler,
  ShogunChopOpenHandler,
  ShogunChopCloseHandler,
  ShogunChopStreamClose,
} from './resources/shogunchop.js';

// The credential holder (sdk ADR-010) — projections
// and transitions only; the instance lives behind `sdk.identity`.
export {
  HolderStore,
  HOLDER_PHASE_KEY,
  IDENTITY_CHANNEL_NAME,
  buildPersistedCredential,
  derivePhaseFromCredential,
  readPersistedCredential,
  resolveIdentityChannel,
  wireEventFor,
} from './identity/holder-store.js';
export type {
  CredentialUnreadable,
  IdentityBusConfig,
  IdentityBusMessage,
  IdentityBusRuntime,
  IdentityWireEvent,
  PersistedCredentialRead,
  TransitionOrigin,
} from './identity/holder-store.js';
export { IdentityController, IdentityNotConfiguredError } from './identity/controller.js';
export type { TeardownBarrier, RenewSource } from './identity/controller.js';
export {
  decideRenewFailure,
  isRefreshDue,
  BROWSER_REFRESH_LEAD_MS,
  RENEW_DEBOUNCE_MS,
  RENEW_BASE_BACKOFF_MS,
  RENEW_MAX_BACKOFF_MS,
} from './identity/renew.js';
export {
  DELEGATED_SESSION_TOKEN,
  isDelegatedSessionToken,
  delegatedMaterial,
  identityOfJwt,
  expiresAtOfJwt,
} from './identity/material.js';
export { MemoryStorageAdapter } from './storage/memory.js';
export { WebStorageAdapter } from './storage/web.js';
export { SDK_STORAGE_NAMESPACE, PERSISTED_CREDENTIAL_VERSION } from './storage/types.js';
export type {
  CredentialUnreadableCause,
  PersistedCredential,
  SDKStorage,
} from './storage/types.js';
export { decideAuthChangedFrame } from './resources/shogunchop.js';
export {
  createNotificationCursorStore,
  notificationCursorKey,
} from './resources/notification-cursor-store.js';
export {
  stashDesktopHandoff,
  claimDesktopHandoff,
  clearDesktopHandoff,
  DESKTOP_HANDOFF_KEY,
} from './identity/handoff-stash.js';
export type { HandoffEntry } from './identity/handoff-stash.js';
export {
  mirrorAction,
  startDaemonArm,
  applyDaemonFrame,
  daemonRenewal,
} from './identity/daemon-arm.js';
export type {
  MirrorAction,
  DaemonRenewalOutcome,
  DaemonArmHandle,
  DaemonFrame,
} from './identity/daemon-arm.js';
export {
  bootAfterResolve,
  bootAfterSnapshot,
  BootRefused,
  publishesClient,
  settleDaemonBoot,
  SingleFlight,
} from './lifecycle/boot.js';
export type {
  BootRefusalCode,
  BootState,
  DaemonBootClient,
  DaemonBootOptions,
  SnapshotFailure,
  SnapshotOutcomeState,
} from './lifecycle/boot.js';
export {
  ALL_CONVERGENCE_STEPS,
  ALL_LAUNCH_REFUSAL_CODES,
  convergenceFailed,
  convergesOnLaunch,
  convergingAfterDiscovery,
  isConvergenceInert,
  launchAfterStart,
  launchOutcome,
  launchRefusal,
  launchRefusalCode,
  sameBinary,
} from './lifecycle/launch.js';
export type {
  BinaryIdentity,
  ConvergenceLive,
  ConvergenceStep,
  LaunchEntry,
  LaunchRefusalCode,
  LaunchState,
  LaunchStateName,
} from './lifecycle/launch.js';
export {
  ALL_BRIDGE_STATES,
  BRIDGE_FRAME_EVENTS,
  BRIDGE_FRAMES,
  bridgeFrameOf,
} from './lifecycle/bridge.js';
export type { BridgeFrameEvent, BridgeState, BridgeStateName } from './lifecycle/bridge.js';
export { LIFECYCLE_BUDGET_MS, withBudget } from './lifecycle/budget.js';
export type { BudgetedOutcome } from './lifecycle/budget.js';
export { emitLifecycleEvent, setLifecycleObserver } from './lifecycle/observability.js';
export type {
  LifecycleEvent,
  LifecycleEventName,
  LifecycleEventFields,
  LifecycleObserver,
} from './lifecycle/observability.js';

// Bootstrap — the resolved-addresses construction path (sdk-source-of-truth
// sdk ADR-008). Browser-safe; the Node resolver rides the ./discovery subpath.
export {
  bootstrapRealm,
  browserWebArmSources,
  createSDK,
  DAEMON_UNRESOLVED,
  declaredWebRuntimeConfigUrl,
  hasDaemonAddress,
  isDaemonUnresolved,
  loadWebRuntimeConfig,
  resolveBrowserBootstrap,
  resolveWebArmBootstrap,
  WEB_REALM,
} from './bootstrap.js';
export type {
  WebArmSources,
  WebRuntimeConfig,
  DaemonAddress,
  DaemonUnresolved,
  SdkBootstrap,
  HostBootstrapChannel,
  CreateSDKOptions,
} from './bootstrap.js';

// ShogunChop discovery — Node.js only, not re-exported from barrel.
// Import directly: import { discoverShogunChop, DiscoveryInfo } from '@localagent/sdk/discovery'

// Documentation
export { DocsIndex } from './docs/index.js';
export type { MethodDocs, ResourceDocs, DocSearchResult, MethodSafety } from './docs/index.js';

// Argument schemas — resolved field-level JSON Schema of each dispatchable
// method's agent-facing arguments (delivered to the agent via docs_read, and
// consumed by the client executor's arg mapping).
export { argSchemaFor } from './docs/arg-schema.js';
export type { ArgSchema, ArgStyle, JsonSchema } from './docs/arg-schema.js';

// Generated dispatch allowlist — the ONLY set of {resource,method} the
// client-side `platform_call` executor may invoke (fail-closed).
export { DISPATCH_ALLOWLIST } from './docs/dispatch.generated.js';
export type { DispatchEntry } from './docs/dispatch.generated.js';

// Bidirectional bridge: `platform_call` executor registry + inbound-frame handler.
export {
  registerPlatformCallExecutor,
  unregisterPlatformCallExecutor,
  handlePlatformCallFrame,
} from './chat/bridge-executor.js';
export type {
  PlatformCallSafety,
  PlatformCallFrameParams,
  PlatformCallResult,
  PlatformCallExecutorFn,
  McpInvokeFrame,
  McpInvokeResponseFrame,
} from './chat/bridge-executor.js';

// UUID helper (getRandomValues-based; safe on insecure http origins).
export { uuidV4 } from './util/uuid.js';

// All types
export type * from './types/index.js';
export {
  WORKSPACE_LIFECYCLES,
  WORKSPACE_ROUTING_CLASSES,
  WORKSPACE_FILESYSTEM_KINDS,
  WORKSPACE_FILESYSTEM_STATES,
  WORKSPACE_WORKSTATION_KINDS,
  WORKSPACE_WORKSTATION_STATUSES,
  WORKSPACE_INDEX_PROGRESS,
  WORKSPACE_INDEX_HEALTH,
  WORKSPACE_ACTIONS,
  WORKSPACE_REFUSAL_REASONS,
  WORKSPACE_OPERATION_KINDS,
  WORKSPACE_OPERATION_STATES,
  WORKSPACE_BINDING_STATES,
} from './types/workspace-state.js';

// Capability error classes (value exports — the types ride the barrel above).
export {
  AmbiguousCapabilityError,
  SessionBusyError,
  WorkspaceRequiredError,
} from './types/capabilities.js';
// Seeded default agent-process IDs (value export — `export type *` above
// does not re-export values).
export { SYSTEM_AGENT_PROCESS, SYSTEM_STEP_TEMPLATE } from './types/agent-process.js';
export {
  TEST_PROVIDER_TYPE,
  TEST_PROVIDER_MODEL,
  TEST_PROVIDER_DEFAULT_REPLY,
  buildTestScript,
  scriptRef,
} from './test-provider.js';
export type { TestScriptRef, TestScriptStep, TestScriptToolCall } from './test-provider.js';
export { ALL_ENTITY_TYPES } from './types/search.js';
export {
  SESSION_LIST_PAGE_SIZE,
  SESSION_LIST_BULK_DELETE_CAP,
} from './resources/session-list.js';
export {
  SESSION_GROUPINGS,
  SESSION_GROUPING_LABELS,
  ALL_FILTER,
  CONVERSATIONS_GROUP_KEY,
  CONVERSATIONS_GROUP_LABEL,
  UNLINKED_GROUP_KEY,
  UNLINKED_GROUP_LABEL,
  WORK_GROUP_KEY,
  WORK_GROUP_LABEL,
  NO_WORKSPACE_GROUP_KEY,
  NO_WORKSPACE_GROUP_LABEL,
} from './types/session-list.js';
export {
  isSessionRunning,
  project,
  flatten,
  selectionInGroup,
  countGroupedSessions,
} from './resources/session-list-project.js';
export type { GroupableSession } from './resources/session-list-project.js';

// Chat API
export { processEvents } from './chat/event-processor.js';
export { normalizeInternalAgentProvenance } from './chat/internal-agent-provenance.js';
export type { InternalAgentProvenance } from './chat/internal-agent-provenance.js';
export { ChatSession } from './chat/chat-session.js';
export {
  HistoryCache,
  deriveHistoryCacheCap,
  historyCacheEntryKey,
  type CachedTailPage,
  type CachedOlderPage,
  type HistoryPageSet,
} from './cache/history-cache.js';
export { PageWindowCache } from './cache/list-cache.js';
export { FeedCache } from './cache/feed-cache.js';
export { feedHomeKey } from './resources/feed.js';
export { browsePageKey, searchPageKey } from './resources/session-list.js';
export { ChatResource, type CreateChatParams, type OpenDraftParams } from './chat/chat-resource.js';
export type * from './chat/types.js';
